import React, { useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';

/**
 * ErrorModal - Displays detailed error information in a modal with intelligent parsing
 * Automatically identifies and highlights error sections (Type, Description, SQL, Hint, etc.)
 * Used for showing import/export errors with formatted details
 */
export default function ErrorModal({ isOpen, title, message, details, onClose }) {
  // Parse error details intelligently
  const parsedError = useMemo(() => {
    if (!details) return null;

    // Log for debugging
    console.log('[ErrorModal] Parsing details:', { 
      detailsType: typeof details,
      detailsLength: details?.length,
      first100: details?.substring(0, 100)
    });

    // Clean the details - handle both escaped and unescaped newlines
    let cleanedDetails = String(details);
    // Replace escaped newlines first
    cleanedDetails = cleanedDetails.replace(/\\n/g, '\n');
    // Also handle escaped quotes
    cleanedDetails = cleanedDetails.replace(/\\"/g, '"');
    
    const lines = cleanedDetails.split('\n');
    
    const sections = {
      errorType: null,
      description: null,
      detail: null,
      hint: null,
      query: null,
      context: null,
      other: []
    };

    let currentSection = 'other';
    let currentContent = [];

    lines.forEach((line) => {
      const trimmed = line.trim();
      
      // Skip empty lines
      if (!trimmed) return;
      
      // Detect section headers - check exact matches first
      if (trimmed.startsWith('Type of error:')) {
        // Finish previous section
        if (currentContent.length > 0) {
          if (currentSection === 'query') sections.query = currentContent.join('\n').trim();
          else if (currentSection === 'context') sections.context = currentContent.join('\n').trim();
        }
        sections.errorType = trimmed.replace(/^Type of error:\s*/i, '').trim();
        currentSection = 'other';
        currentContent = [];
      } else if (trimmed.startsWith('Description of error:')) {
        if (currentContent.length > 0) {
          if (currentSection === 'query') sections.query = currentContent.join('\n').trim();
          else if (currentSection === 'context') sections.context = currentContent.join('\n').trim();
        }
        sections.description = trimmed.replace(/^Description of error:\s*/i, '').trim();
        currentSection = 'other';
        currentContent = [];
      } else if (trimmed.startsWith('Detail:')) {
        if (currentContent.length > 0) {
          if (currentSection === 'query') sections.query = currentContent.join('\n').trim();
          else if (currentSection === 'context') sections.context = currentContent.join('\n').trim();
        }
        sections.detail = trimmed.replace(/^Detail:\s*/i, '').trim();
        currentSection = 'other';
        currentContent = [];
      } else if (trimmed.startsWith('Hint:')) {
        if (currentContent.length > 0) {
          if (currentSection === 'query') sections.query = currentContent.join('\n').trim();
          else if (currentSection === 'context') sections.context = currentContent.join('\n').trim();
        }
        sections.hint = trimmed.replace(/^Hint:\s*/i, '').trim();
        currentSection = 'other';
        currentContent = [];
      } else if (trimmed.startsWith('CONTEXT:')) {
        // Save previous section before starting CONTEXT
        if (currentContent.length > 0) {
          if (currentSection === 'query') sections.query = currentContent.join('\n').trim();
          else if (currentSection === 'context') sections.context = currentContent.join('\n').trim();
        }
        currentSection = 'context';
        // Extract content after "CONTEXT:"
        const content = trimmed.replace(/^CONTEXT:\s*/i, '').trim();
        currentContent = content ? [content] : [];
      } else if (trimmed.toLowerCase().startsWith('sql statement')) {
        // SQL statement/query header
        if (currentContent.length > 0) {
          if (currentSection === 'query') sections.query = currentContent.join('\n').trim();
          else if (currentSection === 'context') sections.context = currentContent.join('\n').trim();
        }
        currentSection = 'query';
        // Extract content after "SQL statement"
        const content = trimmed.replace(/^SQL statement\s*["']?/i, '').replace(/["']$/, '').trim();
        currentContent = content ? [content] : [];
      } else if (trimmed.startsWith('SQL function')) {
        // This is part of query/context - continue accumulating
        if (currentSection === 'context' || currentSection === 'query' || currentSection === 'other') {
          currentContent.push(line);
        }
      } else if (trimmed && currentSection !== 'other') {
        // Continue accumulating content for multi-line sections
        currentContent.push(line);
      } else if (trimmed) {
        // Add to other section
        sections.other.push(line);
      }
    });

    // Save any remaining multi-line content
    if (currentContent.length > 0) {
      if (currentSection === 'query') {
        sections.query = currentContent.join('\n').trim();
      } else if (currentSection === 'context') {
        sections.context = currentContent.join('\n').trim();
      }
    }

    console.log('[ErrorModal] Parsed sections:', sections);
    return sections;
  }, [details]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-red-50 border-b border-red-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-red-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-red-600 hover:text-red-900 focus:outline-none"
            aria-label="Close error modal"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Main message */}
          {message && (
            <div className="mb-2">
              <p className="text-gray-900 font-medium text-sm">{message}</p>
            </div>
          )}

          {/* Error Type - Red highlight */}
          {parsedError?.errorType && (
            <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
              <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">Error Type</p>
              <p className="text-sm text-red-900 mt-1 font-mono">{parsedError.errorType}</p>
            </div>
          )}

          {/* Description - Orange highlight */}
          {parsedError?.description && (
            <div className="bg-orange-50 border-l-4 border-orange-500 p-3 rounded">
              <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Description</p>
              <p className="text-sm text-orange-900 mt-1">{parsedError.description}</p>
            </div>
          )}

          {/* Detail - Yellow highlight */}
          {parsedError?.detail && (
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded">
              <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide">Detail</p>
              <p className="text-sm text-yellow-900 mt-1 font-mono">{parsedError.detail}</p>
            </div>
          )}

          {/* Query - Gray background */}
          {parsedError?.query && (
            <div className="bg-gray-100 border-l-4 border-gray-400 p-3 rounded">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Query</p>
              <pre className="text-xs text-gray-800 mt-2 font-mono whitespace-pre-wrap break-words bg-white p-2 rounded border border-gray-300 max-h-40 overflow-y-auto">
                {parsedError.query}
              </pre>
            </div>
          )}

          {/* Hint - Green highlight */}
          {parsedError?.hint && (
            <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">Hint / Suggestion</p>
              <p className="text-sm text-green-900 mt-1">{parsedError.hint}</p>
            </div>
          )}

          {/* Context - Gray highlight */}
          {parsedError?.context && (
            <div className="bg-gray-50 border-l-4 border-gray-400 p-3 rounded">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Context</p>
              <pre className="text-xs text-gray-700 mt-2 font-mono whitespace-pre-wrap break-words bg-white p-2 rounded border border-gray-300 max-h-40 overflow-y-auto">
                {parsedError.context}
              </pre>
            </div>
          )}

          {/* Other lines - Fallback for unparsed content */}
          {parsedError?.other && parsedError.other.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 p-3 rounded">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Additional Information</p>
              <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap break-words">
                {parsedError.other.join('\n')}
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-6 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Close
          </button>
          {/* Copy to clipboard button */}
          <button
            onClick={() => {
              navigator.clipboard.writeText(details);
              // Show brief feedback (you could integrate with snackbar if desired)
              alert('Error details copied to clipboard!');
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Copy Details
          </button>
        </div>
      </div>
    </div>
  );
}
